/**
 * Filtering utilities for AdsManager table
 * Implements comprehensive filtering logic matching the old system
 */

import { Campaign, Ad, AdsTableFilters } from '../types/ads.types';

/**
 * Check if a value is within a numeric range (inclusive)
 */
function withinRange(
  value: number | null | undefined,
  min: string | undefined,
  max: string | undefined,
): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  const minVal = min && min !== '' ? parseFloat(min) : undefined;
  const maxVal = max && max !== '' ? parseFloat(max) : undefined;

  if (minVal !== undefined && value < minVal) {
    return false;
  }
  if (maxVal !== undefined && value > maxVal) {
    return false;
  }

  return true;
}

/**
 * Check if a text value matches the search query
 */
function matchesTextSearch(
  text: string,
  searchQuery: string,
  mode: 'contains' | 'notContains',
): boolean {
  if (!searchQuery || searchQuery.trim() === '') {
    return true;
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = searchQuery.toLowerCase().trim();

  if (mode === 'contains') {
    return lowerText.includes(lowerQuery);
  } else {
    return !lowerText.includes(lowerQuery);
  }
}

/**
 * Check if an ad passes all metric filters
 */
function adPassesMetricFilters(ad: Ad, filters: AdsTableFilters): boolean {
  return (
    withinRange(ad.spend, filters.minSpend, filters.maxSpend) &&
    withinRange(ad.revenue, filters.minRevenue, filters.maxRevenue) &&
    withinRange(ad.pnl, filters.minPnl, filters.maxPnl) &&
    withinRange(ad.roas, filters.minRoas, filters.maxRoas) &&
    withinRange(ad.rpm, filters.minRpm, filters.maxRpm) &&
    withinRange(ad.rpc, filters.minRpc, filters.maxRpc) &&
    withinRange(ad.cpc, filters.minCpc, filters.maxCpc) &&
    withinRange(ad.cpa, filters.minCpa, filters.maxCpa) &&
    withinRange(ad.coverage, filters.minCoverage, filters.maxCoverage) &&
    withinRange(ad.clicks, filters.minClicks, filters.maxClicks) &&
    withinRange(ad.pageCtr, filters.minPageCtr, filters.maxPageCtr) &&
    withinRange(ad.searches, filters.minSearches, filters.maxSearches)
  );
}

/**
 * Check if a campaign passes metric filters (based on aggregated values)
 */
function campaignPassesMetricFilters(campaign: Campaign, filters: AdsTableFilters): boolean {
  return (
    withinRange(campaign.totalSpend, filters.minSpend, filters.maxSpend) &&
    withinRange(campaign.totalRevenue, filters.minRevenue, filters.maxRevenue) &&
    withinRange(campaign.totalPnl, filters.minPnl, filters.maxPnl) &&
    withinRange(campaign.avgRoas, filters.minRoas, filters.maxRoas) &&
    withinRange(campaign.totalClicks, filters.minClicks, filters.maxClicks) &&
    withinRange(campaign.totalSearches, filters.minSearches, filters.maxSearches)
  );
}

/**
 * Check if a date is within a date range
 */
function withinDateRange(
  date: string | null | undefined,
  fromDate: string | undefined,
  toDate: string | undefined,
): boolean {
  if (!date) {
    return true; // If no date, don't filter
  }

  if (!fromDate && !toDate) {
    return true; // No date range specified
  }

  try {
    const d = new Date(date);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    if (from && d < from) {
      return false;
    }
    if (to && d > to) {
      return false;
    }

    return true;
  } catch {
    return true; // Invalid date format, don't filter
  }
}

/**
 * Check if an ad passes date filters
 */
function adPassesDateFilters(ad: Ad, filters: AdsTableFilters): boolean {
  return (
    withinDateRange(ad.adCreatedTime, filters.adCreatedFrom, filters.adCreatedTo) &&
    withinDateRange(ad.campaignCreatedTime, filters.campaignCreatedFrom, filters.campaignCreatedTo) &&
    withinDateRange(ad.adUpdatedTime, filters.updatedFrom, filters.updatedTo)
  );
}

/**
 * Regroup ads by adset after filtering
 */
function regroupAdsByAdset(ads: Ad[]) {
  const adsetMap = new Map<
    string,
    {
      adsetId: string;
      adsetName: string;
      ads: Ad[];
    }
  >();

  for (const ad of ads) {
    if (!adsetMap.has(ad.adsetId)) {
      adsetMap.set(ad.adsetId, {
        adsetId: ad.adsetId,
        adsetName: ad.adsetName,
        ads: [],
      });
    }
    adsetMap.get(ad.adsetId)!.ads.push(ad);
  }

  return Array.from(adsetMap.values());
}

/**
 * Apply all filters to campaigns
 *
 * @param campaigns - Array of campaigns to filter
 * @param filters - Active filters
 * @param filterScope - Whether to apply filters to campaign totals or individual ads
 * @param searchMode - Text search mode (contains/not contains)
 * @returns Filtered campaigns
 */
export function applyFilters(
  campaigns: Campaign[],
  filters: AdsTableFilters,
  filterScope: 'campaign' | 'ads',
  searchMode: 'contains' | 'notContains',
): Campaign[] {
  return campaigns
    .map((campaign) => {
      // Apply advertiser filter at campaign level
      if (
        filters.advertiser &&
        filters.advertiser.trim() !== '' &&
        !campaign.advertiserName.toLowerCase().includes(filters.advertiser.toLowerCase())
      ) {
        return null;
      }

      // Check if campaign name itself matches the text search
      const campaignSearchText = `${campaign.campaignName || ''} ${campaign.advertiserName || ''} ${campaign.campaignId || ''}`;
      const campaignMatchesSearch = matchesTextSearch(campaignSearchText, filters.campaign, searchMode);

      // Check if campaign passes metric filters (for campaign scope)
      const campaignPassesFilters = campaignPassesMetricFilters(campaign, filters);

      // Filter ads within the campaign
      const filteredByMetrics = campaign.ads.filter((ad) => {
        // Status filter (always apply)
        if (!filters.status.includes(ad.status)) {
          return false;
        }

        // Text search (ad name, adset name, campaign name, ad ID, adset ID, campaign ID) (always apply)
        const adSearchText = `${ad.adName || ''} ${ad.adsetName || ''} ${ad.campaignName || ''} ${ad.adId || ''} ${ad.adsetId || ''} ${ad.campaignId || ''}`;
        if (!matchesTextSearch(adSearchText, filters.campaign, searchMode)) {
          return false;
        }

        // Date filters (always apply)
        if (!adPassesDateFilters(ad, filters)) {
          return false;
        }

        // Metric filters (only apply when filterScope === 'ads')
        if (filterScope === 'ads' && !adPassesMetricFilters(ad, filters)) {
          return false;
        }

        return true;
      });

      let adsToShow: Ad[];
      let includeCampaign = false;

      if (filterScope === 'campaign') {
        // When filtering by campaign, show all ads only if the campaign itself passes all numeric filters
        if (campaignPassesFilters) {
          adsToShow = filteredByMetrics; // Show ads that pass status/text/date filters
          includeCampaign = true;
        } else {
          adsToShow = [];
          includeCampaign = false;
        }
      } else {
        // When filtering by ads, show only ads that pass metric filters
        adsToShow = filteredByMetrics;
        // Include campaign if campaign passes OR there are any passing ads
        includeCampaign = campaignPassesFilters || adsToShow.length > 0;
      }

      // IMPORTANT: If no text search is active, OR campaign name matches, OR any ads match
      // then we should include the campaign (if it has matching ads)
      // This allows searching for Ad IDs to work even if campaign name doesn't match
      if (!filters.campaign || filters.campaign.trim() === '') {
        // No search filter - use existing logic
      } else if (campaignMatchesSearch) {
        // Campaign name matches search - always include if has ads
        includeCampaign = adsToShow.length > 0;
      } else if (adsToShow.length > 0) {
        // Campaign name doesn't match, but some ads do - include the campaign
        includeCampaign = true;
      }

      // If campaign should not be included, exclude it
      if (!includeCampaign || adsToShow.length === 0) {
        return null;
      }

      // Recalculate campaign totals based on adsToShow
      const totalSpend = adsToShow.reduce((sum, ad) => sum + ad.spend, 0);
      const totalRevenue = adsToShow.reduce((sum, ad) => sum + ad.revenue, 0);
      const totalPnl = adsToShow.reduce((sum, ad) => sum + ad.pnl, 0);
      const totalClicks = adsToShow.reduce((sum, ad) => sum + ad.clicks, 0);
      const totalSearches = adsToShow.reduce((sum, ad) => sum + ad.searches, 0);
      const avgRoas = totalSpend > 0 ? totalRevenue / totalSpend : 0;

      // Return campaign with filtered ads and recalculated totals
      return {
        ...campaign,
        ads: adsToShow,
        totalSpend,
        totalRevenue,
        totalPnl,
        totalClicks,
        totalSearches,
        avgRoas,
        // Recalculate adsets based on filtered ads
        adsets: regroupAdsByAdset(adsToShow),
      };
    })
    .filter((campaign): campaign is Campaign => campaign !== null);
}

/**
 * Get default filters
 */
export function getDefaultFilters(): AdsTableFilters {
  return {
    status: ['ACTIVE', 'PAUSED'], // Default: show ACTIVE and PAUSED, exclude UNKNOWN, ARCHIVED, DELETED
    campaign: '',
    advertiser: '',
    minSpend: '0.01', // Default: exclude $0 spend
    maxSpend: '',
    minRevenue: '',
    maxRevenue: '',
    minPnl: '',
    maxPnl: '',
    minRoas: '',
    maxRoas: '',
    minRpm: '',
    maxRpm: '',
    minRpc: '',
    maxRpc: '',
    minCpc: '',
    maxCpc: '',
    minCpa: '',
    maxCpa: '',
    minCoverage: '',
    maxCoverage: '',
    minClicks: '',
    maxClicks: '',
    minPageCtr: '',
    maxPageCtr: '',
    minSearches: '',
    maxSearches: '',
    adCreatedFrom: '',
    adCreatedTo: '',
    campaignCreatedFrom: '',
    campaignCreatedTo: '',
    updatedFrom: '',
    updatedTo: '',
  };
}
